import React, { useState, useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaUserMd, FaPhone, FaStar, FaCalendarAlt, FaClock, FaPlus, FaUserPlus, FaSearch } from 'react-icons/fa';
import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastHelper';
import { apiGet } from '../../../shared/utils/apiConfig';
import { fetchClinicDoctors, inviteDoctor, clearError, clearSuccess } from '../../../redux/Slices/ClinicDoctorsSlice';

const ClinicDoctors = () => {
    const { setTitle } = useTitle();
    const dispatch = useDispatch();
    const { doctors, loading, error, success, pagination } = useSelector((state) => state.clinicDoctors);
    const [currentPage, setCurrentPage] = useState(1);

    const [showInviteModal, setShowInviteModal] = useState(false);
    const [reservationsModal, setReservationsModal] = useState({ isOpen: false, doctor: null });
    const [workingDaysModal, setWorkingDaysModal] = useState({ isOpen: false, doctor: null });
    const [doctorReservations, setDoctorReservations] = useState([]);
    const [loadingReservations, setLoadingReservations] = useState(false);
    const [inviteCode, setInviteCode] = useState('');

    useEffect(() => {
        setTitle('الأطباء');
    }, [setTitle]);

    useEffect(() => {
        dispatch(fetchClinicDoctors({ page: currentPage }));
    }, [dispatch, currentPage]);


    useEffect(() => {
        if (error) {
            showErrorToast(error);
            dispatch(clearError());
        }
    }, [error, dispatch]);

    useEffect(() => {
        if (success) {
            showSuccessToast(success);
            dispatch(clearSuccess());
            setShowInviteModal(false);
            setInviteCode('');
        }
    }, [success, dispatch]);

    const handleInviteDoctor = async (e) => {
        e.preventDefault();
        if (!inviteCode.trim()) {
            showErrorToast('يرجى إدخال كود الدعوة');
            return;
        }

        try {
            await dispatch(inviteDoctor({ code: inviteCode.trim() })).unwrap();
        } catch (error) {
            // Error handled by Redux
        }
    };

    const [reservationFilters, setReservationFilters] = useState({ fromDate: '', toDate: '' });

    const openReservations = async (doctor) => {
        setReservationsModal({ isOpen: true, doctor });
        setDoctorReservations([]);
        setReservationFilters({ fromDate: '', toDate: '' });
        await fetchDoctorReservations(doctor?.id || doctor?.doctor_id);
    };

    const fetchDoctorReservations = async (doctorId) => {
        if (!doctorId) return;
        try {
            setLoadingReservations(true);
            const queryParams = new URLSearchParams();
            if (reservationFilters.fromDate) queryParams.append('from_date', reservationFilters.fromDate);
            if (reservationFilters.toDate) queryParams.append('to_date', reservationFilters.toDate);
            const url = queryParams.toString()
                ? `/clinics_dash/doctor_reservations/${doctorId}?${queryParams.toString()}`
                : `/clinics_dash/doctor_reservations/${doctorId}`;
            const response = await apiGet(url);
            // Handle different response structures
            let list = [];
            if (Array.isArray(response)) {
                list = response;
            } else if (Array.isArray(response?.result)) {
                list = response.result;
            } else if (Array.isArray(response?.data)) {
                list = response.data;
            } else if (response?.result?.data && Array.isArray(response.result.data)) {
                list = response.result.data;
            }
            setDoctorReservations(list);
        } catch (err) {
            showErrorToast('فشل تحميل حجوزات الطبيب');
            setDoctorReservations([]);
        } finally {
            setLoadingReservations(false);
        }
    };

    const getDayName = (day) => {
        const days = {
            'saturday': 'السبت',
            'sunday': 'الأحد',
            'monday': 'الاثنين',
            'tuesday': 'الثلاثاء',
            'wednesday': 'الأربعاء',
            'thursday': 'الخميس',
            'friday': 'الجمعة'
        };
        return days[day] || day;
    };

    const formatTime = (time) => {
        return time || 'غير محدد';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return date.toLocaleDateString('ar-EG', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">جاري تحميل قائمة الأطباء...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content clinic-doctors-page">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">الأطباء</h4>
                    <motion.button
                        className="btn btn-primary"
                        onClick={() => setShowInviteModal(true)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '10px 20px',
                            fontSize: '14px',
                            fontWeight: '600',
                            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                        }}
                    >
                        <FaUserPlus className="mx-2" />
                        دعوة طبيب
                    </motion.button>
                </div>

                {/* Doctors Table */}
                <div className="card shadow-lg border-0">
                    <div className="card-body">
                        {!Array.isArray(doctors) || doctors.length === 0 ? (
                            <div className="text-center py-5">
                                <FaUserMd className="text-muted" style={{ fontSize: '4rem' }} />
                                <h5 className="mt-3 text-muted">لا توجد أطباء</h5>
                                <p className="text-muted">لم يتم إضافة أي أطباء بعد</p>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-modern">
                                        <thead>
                                            <tr>
                                                <th className="text-center">#</th>
                                                <th className="text-center">الصورة</th>
                                                <th className="text-center">اسم الطبيب</th>
                                                <th className="text-center">التخصص</th>
                                                <th className="text-center">رقم الهاتف</th>
                                                <th className="text-center">التقييم</th>
                                                <th className="text-center">أيام العمل</th>
                                                <th className="text-center">الإجراءات</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {doctors.map((doctor, index) => (
                                                <motion.tr
                                                    key={doctor.id}
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: index * 0.1 }}
                                                    className="table-row-modern"
                                                >
                                                    <td className="text-center">
                                                        <div className="order-number-badge">
                                                            {index + 1}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="doctor-image-container">
                                                            {doctor.image ? (
                                                                <img
                                                                    src={doctor.image}
                                                                    alt={doctor.name}
                                                                    className="doctor-image"
                                                                />
                                                            ) : (
                                                                <div className="doctor-placeholder">
                                                                    <FaUserMd />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="doctor-name">
                                                            {doctor.name || 'غير محدد'}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="doctor-specialty">
                                                            {doctor.description || 'غير محدد'}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="phone-badge">
                                                            <FaPhone className="mx-1" />
                                                            {doctor.phone || 'غير محدد'}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="rating-display">
                                                            <FaStar className="text-warning mx-1" />
                                                            <span className="fw-semibold">
                                                                {doctor.rate_avg ? doctor.rate_avg.toFixed(1) : '0.0'}
                                                            </span>
                                                            <small className="text-muted d-block">
                                                                ({doctor.rate_count || 0} تقييم)
                                                            </small>
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="working-days-display">
                                                            {doctor.doctor_clinic_days && doctor.doctor_clinic_days.length > 0 ? (
                                                                <div>
                                                                    {doctor.doctor_clinic_days.slice(0, 1).map((day, dayIndex) => (
                                                                        <div key={dayIndex} className="working-day-item-small mb-2">
                                                                            <small className="fw-medium">
                                                                                {getDayName(day.day)}
                                                                            </small>
                                                                            <br />
                                                                            <small className="text-muted">
                                                                                {formatTime(day.from)} - {formatTime(day.to)}
                                                                            </small>
                                                                            {day.slots && day.slots.length > 0 && (
                                                                                <small className="d-block text-success" style={{ fontSize: '0.7rem' }}>
                                                                                    ({day.slots.length} مواعيد)
                                                                                </small>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                    {doctor.doctor_clinic_days.length > 1 && (
                                                                        <small className="text-muted d-block mb-2">
                                                                            +{doctor.doctor_clinic_days.length - 1} أيام أخرى
                                                                        </small>
                                                                    )}
                                                                    <button
                                                                        className="btn btn-sm btn-link p-0 mt-1"
                                                                        style={{ fontSize: '0.85rem', textDecoration: 'none', color: '#667eea' }}
                                                                        onClick={() => setWorkingDaysModal({ isOpen: true, doctor })}
                                                                    >
                                                                        اقرأ المزيد
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted">لا توجد مواعيد</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="action-buttons">
                                                            <motion.button
                                                                whileHover={{ scale: 1.1, y: -2 }}
                                                                whileTap={{ scale: 0.95 }}
                                                                className="btn btn-primary ms-2"
                                                                title="عرض حجوزات الطبيب"
                                                                onClick={() => openReservations(doctor)}
                                                            >
                                                                <FaCalendarAlt className="mx-1" />
                                                                الحجوزات
                                                            </motion.button>
                                                        </div>
                                                    </td>
                                                </motion.tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {doctors && doctors.length > 0 && (
                                    <nav className="mt-3 d-flex justify-content-center">
                                        <ul className="pagination mb-0">
                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                <button className="page-link" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>
                                                    السابق
                                                </button>
                                            </li>
                                            {Array.from({ length: (pagination?.pages_count || 1) }).map((_, i) => (
                                                <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                                    <button className="page-link" onClick={() => setCurrentPage(i + 1)}>
                                                        {i + 1}
                                                    </button>
                                                </li>
                                            ))}
                                            <li className={`page-item ${currentPage === (pagination?.pages_count || 1) ? 'disabled' : ''}`}>
                                                <button className="page-link" onClick={() => setCurrentPage((p) => Math.min((pagination?.pages_count || 1), p + 1))}>
                                                    التالي
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Invite Doctor Modal */}
                {showInviteModal && (
                    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header d-flex justify-content-between align-items-center">
                                    <h5 className="modal-title mb-0">
                                        <FaUserPlus className="mx-2" />
                                        دعوة طبيب جديد
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => {
                                            setShowInviteModal(false);
                                            setInviteCode('');
                                        }}
                                    ></button>
                                </div>
                                <form onSubmit={handleInviteDoctor}>
                                    <div className="modal-body">
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold">كود الدعوة</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={inviteCode}
                                                onChange={(e) => setInviteCode(e.target.value)}
                                                placeholder="أدخل كود الدعوة"
                                                required
                                                style={{
                                                    border: '2px solid #e9ecef',
                                                    borderRadius: '8px',
                                                    padding: '12px 16px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <div className="form-text">
                                                اطلب من الطبيب إرسال كود الدعوة الخاص به
                                            </div>
                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => {
                                                setShowInviteModal(false);
                                                setInviteCode('');
                                            }}
                                        >
                                            إلغاء
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            style={{
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                border: 'none'
                                            }}
                                        >
                                            إرسال الدعوة
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}
                {/* Working Days Modal */}
                {workingDaysModal.isOpen && workingDaysModal.doctor && (
                    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-lg modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header d-flex justify-content-between align-items-center">
                                    <h5 className="modal-title mb-0">
                                        <FaClock className="mx-2" />
                                        أيام عمل {workingDaysModal.doctor?.name || ''}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setWorkingDaysModal({ isOpen: false, doctor: null })}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    {workingDaysModal.doctor.doctor_clinic_days && workingDaysModal.doctor.doctor_clinic_days.length > 0 ? (
                                        <div className="row g-3">
                                            {workingDaysModal.doctor.doctor_clinic_days.map((day, dayIndex) => (
                                                <div key={dayIndex} className="col-md-6">
                                                    <div className="card border-0 shadow-sm h-100">
                                                        <div className="card-body">
                                                            <h6 className="card-title fw-bold mb-3">
                                                                <FaCalendarAlt className="mx-2 text-primary" />
                                                                {getDayName(day.day)}
                                                            </h6>
                                                            <div className="mb-2">
                                                                <small className="text-muted">من:</small>
                                                                <span className="fw-semibold ms-2">{formatTime(day.from)}</span>
                                                            </div>
                                                            <div className="mb-2">
                                                                <small className="text-muted">إلى:</small>
                                                                <span className="fw-semibold ms-2">{formatTime(day.to)}</span>
                                                            </div>
                                                            {day.slot_duration && (
                                                                <div className="mb-2">
                                                                    <small className="text-muted">مدة الموعد:</small>
                                                                    <span className="fw-semibold ms-2">{day.slot_duration} دقيقة</span>
                                                                </div>
                                                            )}
                                                            {day.slots && day.slots.length > 0 && (
                                                                <div className="mt-3">
                                                                    <small className="text-muted d-block mb-2">المواعيد المتاحة:</small>
                                                                    <div className="d-flex flex-wrap gap-2">
                                                                        {day.slots.map((slot, slotIndex) => (
                                                                            <span
                                                                                key={slotIndex}
                                                                                className={`badge ${slot.status === 'open' ? 'bg-success' : 'bg-secondary'}`}
                                                                                style={{ fontSize: '0.75rem' }}
                                                                            >
                                                                                {slot.slot_time} {slot.status === 'open' ? '(متاح)' : '(محتجز)'}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                    <small className="text-success d-block mt-2">
                                                                        إجمالي المواعيد: {day.slots.length}
                                                                    </small>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-muted mb-0 text-center">لا توجد أيام عمل محددة</p>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setWorkingDaysModal({ isOpen: false, doctor: null })}
                                    >
                                        إغلاق
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {/* Doctor Reservations Modal */}
                {reservationsModal.isOpen && (
                    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-xl modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header d-flex justify-content-between align-items-center">
                                    <h5 className="modal-title mb-0">
                                        <FaCalendarAlt className="mx-2" />
                                        حجوزات الطبيب {reservationsModal.doctor?.name || ''}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setReservationsModal({ isOpen: false, doctor: null })}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    {/* Filters */}
                                    <div className="card mb-3">
                                        <div className="card-body">
                                            <div className="row g-2">
                                                <div className="col-md-4">
                                                    <label className="form-label small">من تاريخ</label>
                                                    <input
                                                        type="date"
                                                        className="form-control form-control-sm"
                                                        value={reservationFilters.fromDate}
                                                        onChange={(e) => setReservationFilters({ ...reservationFilters, fromDate: e.target.value })}
                                                    />
                                                </div>
                                                <div className="col-md-4">
                                                    <label className="form-label small">إلى تاريخ</label>
                                                    <input
                                                        type="date"
                                                        className="form-control form-control-sm"
                                                        value={reservationFilters.toDate}
                                                        onChange={(e) => setReservationFilters({ ...reservationFilters, toDate: e.target.value })}
                                                    />
                                                </div>
                                                <div className="col-md-4 d-flex align-items-end">
                                                    <button
                                                        className="btn btn-primary btn-sm w-100"
                                                        onClick={() => fetchDoctorReservations(reservationsModal.doctor?.id || reservationsModal.doctor?.doctor_id)}
                                                        disabled={loadingReservations}
                                                    >
                                                        <FaSearch className="mx-1" />
                                                        بحث
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {loadingReservations ? (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    ) : !doctorReservations.length ? (
                                        <p className="text-muted mb-0 text-center">لا توجد حجوزات</p>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-modern">
                                                <thead>
                                                    <tr>
                                                        <th className="text-center">#</th>
                                                        <th className="text-center">اسم المريض</th>
                                                        <th className="text-center">الهاتف</th>
                                                        <th className="text-center">التاريخ</th>
                                                        <th className="text-center">اليوم</th>
                                                        <th className="text-center">الميعاد</th>
                                                        <th className="text-center">نوع الحجز</th>
                                                        <th className="text-center">السعر</th>
                                                        <th className="text-center">حالة الدفع</th>
                                                        <th className="text-center">الحالة</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {doctorReservations.map((r, idx) => (
                                                        <tr key={r.id ?? idx}>
                                                            <td className="text-center">{idx + 1}</td>
                                                            <td className="text-center">{r.patient_name || r.client_name || '-'}</td>
                                                            <td className="text-center">{r.patient_phone || r.client_phone || '-'}</td>
                                                            <td className="text-center">{formatDate(r.date)}</td>
                                                            <td className="text-center">{r.day ? getDayName(r.day) : '-'}</td>
                                                            <td className="text-center">{r.slot || r.time || '-'}</td>
                                                            <td className="text-center">{r.appointment_type || '-'}</td>
                                                            <td className="text-center">{r.appointment_price ? `${r.appointment_price} جنيه` : '-'}</td>
                                                            <td className="text-center">
                                                                {r.payment_status ? (
                                                                    <span className={`badge ${
                                                                        r.payment_status === 'paid' ? 'bg-success' :
                                                                        r.payment_status === 'pending' ? 'bg-warning' :
                                                                        r.payment_status === 'failed' ? 'bg-danger' : 'bg-secondary'
                                                                    } text-white`}>
                                                                        {r.payment_status === 'paid' ? 'مدفوع' :
                                                                         r.payment_status === 'pending' ? 'قيد الانتظار' :
                                                                         r.payment_status === 'failed' ? 'فشل' : r.payment_status}
                                                                    </span>
                                                                ) : '-'}
                                                                {r.pay_type && (
                                                                    <small className="d-block text-muted" style={{ fontSize: '0.7rem' }}>
                                                                        {r.pay_type === 'cash' ? 'نقدي' : r.pay_type === 'online' ? 'أونلاين' : r.pay_type}
                                                                    </small>
                                                                )}
                                                            </td>
                                                            <td className="text-center">
                                                                <span className={`badge ${
                                                                    r.status === 'reseved' ? 'bg-success' :
                                                                    r.status === 'cancelled' ? 'bg-danger' :
                                                                    'bg-secondary'
                                                                } text-white`}>
                                                                    {r.status === 'reseved' ? 'محجوز' :
                                                                     r.status === 'cancelled' ? 'ملغي' :
                                                                     r.status || '-'}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setReservationsModal({ isOpen: false, doctor: null })}
                                    >
                                        إغلاق
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* CSS Styles */}
            <style jsx>{`
                .clinic-doctors-page .table-modern td {
                    vertical-align: middle;
                }

                .clinic-doctors-page .doctor-specialty {
                    display: block;
                    max-width: 260px;
                    margin: 0 auto;
                    white-space: normal;
                    word-break: break-word;
                    color: #4a5568;
                    line-height: 1.6;
                }

                .clinic-doctors-page .phone-badge {
                    background: #f0f9ff;
                    color: #0369a1;
                    padding: 0.45rem 0.9rem;
                    border-radius: 10px;
                    font-weight: 600;
                    border: 1px solid #bae6fd;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.4rem;
                    justify-content: center;
                    direction: ltr;
                    min-width: 150px;
                    max-width: 100%;
                    white-space: nowrap;
                    box-shadow: inset 0 0 0 1px rgba(191, 219, 254, 0.35);
                }

                .doctor-card {
                    border-radius: 16px;
                    transition: all 0.3s ease;
                    background: linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%);
                }
                
                .doctor-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 30px rgba(0,0,0,0.15) !important;
                }
                
                .doctor-image-container {
                    position: relative;
                    display: inline-block;
                }
                
                .doctor-image {
                    width: 80px;
                    height: 80px;
                    border-radius: 50%;
                    object-fit: cover;
                    border: 4px solid #fff;
                    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
                }
                
                .doctor-placeholder {
                    width: 80px;
                    height: 80px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 2rem;
                    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
                }
                
                .doctor-name {
                    color: #2d3748;
                    font-weight: 600;
                    margin-bottom: 0.5rem;
                }
                
                .doctor-specialty {
                    color: #718096;
                    font-size: 0.9rem;
                }
                
                .working-days-section {
                    border-top: 1px solid #e2e8f0;
                    padding-top: 1rem;
                }
                
                .working-day-item {
                    background: #f7fafc;
                    padding: 0.75rem;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                }
                
                .slots-info {
                    margin-top: 0.25rem;
                }
                
                .modal-content {
                    border-radius: 12px;
                    border: none;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.2);
                }
                
                .modal-header {
                    border-bottom: 1px solid #e2e8f0;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    border-radius: 12px 12px 0 0;
                }
                
                .modal-header .btn-close {
                    filter: invert(1);
                    margin-right : auto !important;
                    margin-left : 0 !important;
                }
            `}</style>
        </div>
    );
};

export default ClinicDoctors;
